import {
  WEEKLY_RECAP_J_DAYS,
  WEEKLY_RECAP_REFERENCE_METRICS,
  WEEKLY_RECAP_INTERNAL_METRICS,
  WEEKLY_RECAP_DEFAULT_DAY_PERCENTAGES,
  createDefaultPercentageMatrix,
  normalizeMicrocycleConfig,
  toFiniteNumber,
  filterWeeklyRecapLoadMetrics,
} from "./weeklyRecapConfig";

function toNullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function buildPlannedMatrix({ referenceValues, planPercentages, metrics, jDays }) {
  return Object.fromEntries(
    metrics.map((metric) => {
      const reference = toNullableNumber(referenceValues?.[metric.key]);
      const valuesByDay = Object.fromEntries(
        jDays.map((day) => {
          const pct = toFiniteNumber(planPercentages?.[metric.key]?.[day], 0);
          const value = reference === null ? null : (reference * pct) / 100;
          return [day, value];
        }),
      );
      return [metric.key, valuesByDay];
    }),
  );
}

function computeSimilarityPercent(plannedTotal, actualTotal) {
  const p = toNullableNumber(plannedTotal);
  const a = toNullableNumber(actualTotal);

  if (p === null || a === null) return null;

  const plannedMagnitude = Math.abs(p);
  const actualMagnitude = Math.abs(a);

  if (plannedMagnitude === 0 && actualMagnitude === 0) return null;
  if (plannedMagnitude === 0 || actualMagnitude === 0) return 0;

  const high = Math.max(plannedMagnitude, actualMagnitude);
  const low = Math.min(plannedMagnitude, actualMagnitude);
  return Math.max(0, Math.min(100, (low / high) * 100));
}

function computeAverageSimilarity(values) {
  const numeric = (values || [])
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (!numeric.length) return null;
  return numeric.reduce((acc, value) => acc + value, 0) / numeric.length;
}

export const MAQUETTE_CLUB_ID = "7c61d7a5-7da7-409a-9bfd-18416640f640";

const DEMO_OPPONENTS = ["Paris FC", "Troyes", "Nancy", "Quevilly", "Châteauroux", "Grenoble"];

const POSITION_REFERENCE_PROFILES = {
  Forward: {
    global_distance: 10.8,
    hsr_distance_m: 920,
    spr_distance_m: 380,
    sprint_count: 32,
    sprint_max: 33.6,
    vmax: 34.1,
    COD_number: 48,
    acceleration_max: 4.35,
    deceleration_max: -4.65,
    hr: 174,
    mechanical_fatigue: 6.4,
    intensity: 82,
    mechanical_load: 76,
  },
  Midfield: {
    global_distance: 11.4,
    hsr_distance_m: 780,
    spr_distance_m: 260,
    sprint_count: 24,
    sprint_max: 32.1,
    vmax: 32.6,
    COD_number: 52,
    acceleration_max: 4.05,
    deceleration_max: -4.35,
    hr: 168,
    mechanical_fatigue: 5.8,
    intensity: 74,
    mechanical_load: 70,
  },
  Defense: {
    global_distance: 9.6,
    hsr_distance_m: 620,
    spr_distance_m: 180,
    sprint_count: 16,
    sprint_max: 31.2,
    vmax: 31.7,
    COD_number: 38,
    acceleration_max: 3.85,
    deceleration_max: -4.15,
    hr: 162,
    mechanical_fatigue: 5.2,
    intensity: 66,
    mechanical_load: 62,
  },
  Lateral: {
    global_distance: 10.2,
    hsr_distance_m: 840,
    spr_distance_m: 310,
    sprint_count: 26,
    sprint_max: 32.8,
    vmax: 33.3,
    COD_number: 44,
    acceleration_max: 4.15,
    deceleration_max: -4.45,
    hr: 170,
    mechanical_fatigue: 6.0,
    intensity: 76,
    mechanical_load: 72,
  },
};

const DEFAULT_PROFILE = POSITION_REFERENCE_PROFILES.Midfield;

/** Day-level load shape vs plan (post-match microcycle). */
const DAY_ACTUAL_FACTORS = {
  "MD+1": 0.86,
  "MD+2": 0.91,
  "MD+3": 1.04,
  "MD+4": 0.97,
  "MD+5": 1.03,
  "MD+6": 0.88,
};

function hashSeed(value) {
  const str = String(value ?? "");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) || 1;
}

function seededUnit(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function round(value, decimals = 2) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

function formatDisplayDate(date) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function getRecentMatchSaturdays(count = 6) {
  const dates = [];
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);

  while (cursor.getDay() !== 6) {
    cursor.setDate(cursor.getDate() - 1);
  }

  for (let i = 0; i < count; i += 1) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() - 7);
  }

  return dates;
}

export function isMaquetteClub(clubId) {
  return String(clubId || "") === MAQUETTE_CLUB_ID;
}

function resolveReferenceProfile(player) {
  const position = player?.position || "Midfield";
  return POSITION_REFERENCE_PROFILES[position] || DEFAULT_PROFILE;
}

function buildPlayerSeed(player, targetMatchDate) {
  return hashSeed(`${player?.id || "player"}::${player?.last_name || ""}::${targetMatchDate || "default"}`);
}

function resolveMatchIndex(matchDates, targetIso) {
  if (!targetIso) return 0;
  const index = matchDates.findIndex((date) => toIsoDate(date) === targetIso);
  return index >= 0 ? index : 0;
}

function buildDayActualFactors(matchIndex, seed) {
  const matchShift = matchIndex * 0.025;
  return Object.fromEntries(
    Object.entries(DAY_ACTUAL_FACTORS).map(([day, factor]) => {
      const noise = (seededUnit(seed + matchIndex * 11 + day.length * 3) - 0.5) * 0.08;
      return [day, factor + matchShift + noise];
    }),
  );
}

function scaleReferenceProfile(profile, seed) {
  const variance = 0.88 + seededUnit(seed) * 0.22;
  return Object.fromEntries(
    Object.entries(profile).map(([key, value]) => {
      const metricVariance = variance + (seededUnit(seed + key.length * 17) - 0.5) * 0.08;
      return [key, round(Number(value) * metricVariance, key === "sprint_count" || key === "COD_number" ? 0 : 2)];
    }),
  );
}

function buildActualMatrix({ referenceValues, planPercentages, metrics, jDays, seed, dayFactors }) {
  return Object.fromEntries(
    metrics.map((metric) => {
      const reference = toFiniteNumber(referenceValues[metric.key], 0);
      const byDay = Object.fromEntries(
        jDays.map((day, dayIndex) => {
          if (day === "MD") {
            return [day, null];
          }

          const pct = toFiniteNumber(planPercentages?.[metric.key]?.[day], 0);
          const planned = reference * (pct / 100);
          const dayFactor = dayFactors?.[day] ?? DAY_ACTUAL_FACTORS[day] ?? 0.95;
          const noise = 0.94 + seededUnit(seed + dayIndex * 31 + metric.key.length * 7) * 0.12;
          const actual = planned * dayFactor * noise;

          const decimals = Number.isInteger(metric.decimals) ? metric.decimals : 2;
          return [day, round(actual, decimals)];
        }),
      );
      return [metric.key, byDay];
    }),
  );
}

function overlayPeakSpeedActuals(actualValues, referenceValues, jDays, seed) {
  const next = { ...(actualValues || {}) };
  ["vmax", "sprint_max"].forEach((key) => {
    const reference = toNullableNumber(referenceValues?.[key]);
    next[key] = Object.fromEntries(
      jDays.map((day, index) => {
        if (day === "MD" || reference === null) return [day, null];
        const factor = 0.90 + seededUnit(seed + key.length * 19 + index * 7) * 0.12;
        return [day, round(reference * factor, 1)];
      }),
    );
  });
  return next;
}

function computeSimilarityByMetric({ plannedMatrix, actualMatrix, metrics, jDays }) {
  return Object.fromEntries(
    metrics.map((metric) => {
      let plannedTotal = 0;
      let actualTotal = 0;
      let hasValue = false;

      jDays.forEach((day) => {
        if (day === "MD") return;
        const planned = plannedMatrix?.[metric.key]?.[day];
        const actual = actualMatrix?.[metric.key]?.[day];
        if (planned === null || planned === undefined || actual === null || actual === undefined) {
          return;
        }
        hasValue = true;
        plannedTotal += Number(planned) || 0;
        actualTotal += Number(actual) || 0;
      });

      if (!hasValue) return [metric.key, null];
      return [metric.key, computeSimilarityPercent(plannedTotal, actualTotal)];
    }),
  );
}

function buildSimilarityHistory({ matchDates, selectedTargetIso, seed, weeklySimilarity }) {
  const selectedIndex = resolveMatchIndex(matchDates, selectedTargetIso);
  const priorDates = matchDates.slice(selectedIndex + 1);

  const history = priorDates.slice(0, 5).reverse().map((date, index) => {
    const drift = (seededUnit(seed + index * 53) - 0.5) * 18;
    const pct = Math.max(58, Math.min(96, weeklySimilarity + drift - index * 1.5));
    const calculatedAt = new Date(date);
    calculatedAt.setDate(calculatedAt.getDate() + 6);

    return {
      target_match_date: toIsoDate(date),
      today: toIsoDate(calculatedAt),
      calculated_at: calculatedAt.toISOString(),
      weekly_similarity: round(pct, 1),
      external_weekly_similarity: round(pct + (seededUnit(seed + index) - 0.5) * 4, 1),
    };
  });

  return history;
}

function buildAvailableTargetMatches(matchDates, selectedDate) {
  const selectedIso = selectedDate ? toIsoDate(selectedDate) : null;
  const latestCompleted = matchDates[0] ? toIsoDate(matchDates[0]) : null;

  return matchDates.map((date, index) => {
    const iso = toIsoDate(date);
    return {
      date: iso,
      label: formatDisplayDate(date),
      opponent: DEMO_OPPONENTS[index % DEMO_OPPONENTS.length],
      is_selected: iso === selectedIso,
      is_completed: true,
      is_selectable: true,
      is_latest_completed: iso === latestCompleted,
      player_has_match_data: true,
      status: "completed",
    };
  });
}

export function buildMaquetteGlobalPlan({
  season,
  metrics = [...WEEKLY_RECAP_REFERENCE_METRICS, ...WEEKLY_RECAP_INTERNAL_METRICS],
  fallbackJDays = WEEKLY_RECAP_J_DAYS,
}) {
  const jDays = fallbackJDays;
  const microcycle = normalizeMicrocycleConfig(null);
  const matchDates = getRecentMatchSaturdays(6);
  const targetMatchDate = matchDates[0] ? toIsoDate(matchDates[0]) : null;

  return {
    source: "maquette_mock",
    endpoint: "mock://weekly-recap/plan",
    jDays,
    planPercentages: createDefaultPercentageMatrix(metrics, jDays, WEEKLY_RECAP_DEFAULT_DAY_PERCENTAGES),
    displayConfig: null,
    microcycle,
    calculateSimilarityFromDay: microcycle.calculate_similarity_from,
    nextMatchDate: targetMatchDate,
    nextMatchDay: "MD",
    targetMatchDate,
    availableTargetMatches: buildAvailableTargetMatches(matchDates, matchDates[0]),
    season,
    error: null,
  };
}

export function buildMaquettePlayerBundle({
  player,
  season,
  metrics = [...WEEKLY_RECAP_REFERENCE_METRICS, ...WEEKLY_RECAP_INTERNAL_METRICS],
  jDays = WEEKLY_RECAP_J_DAYS,
  targetMatchDate,
  globalPlanPercentages,
}) {
  const matchDates = getRecentMatchSaturdays(6);
  const resolvedTargetDate = targetMatchDate
    ? new Date(`${targetMatchDate}T00:00:00`)
    : matchDates[0];
  const targetIso = resolvedTargetDate && !Number.isNaN(resolvedTargetDate.getTime())
    ? toIsoDate(resolvedTargetDate)
    : (matchDates[0] ? toIsoDate(matchDates[0]) : null);

  const matchIndex = resolveMatchIndex(matchDates, targetIso);
  const seed = buildPlayerSeed(player, targetIso);
  const dayFactors = buildDayActualFactors(matchIndex, seed);

  const baseProfile = scaleReferenceProfile(resolveReferenceProfile(player), seed);
  const matchVariance = 0.92 + seededUnit(seed + matchIndex * 29) * 0.14;
  const referenceValues = Object.fromEntries(
    Object.entries(baseProfile).map(([key, value]) => {
      const decimals = key === "sprint_count" || key === "COD_number" ? 0 : 2;
      return [key, round(Number(value) * matchVariance, decimals)];
    }),
  );

  const planPercentages = globalPlanPercentages
    || createDefaultPercentageMatrix(metrics, jDays, WEEKLY_RECAP_DEFAULT_DAY_PERCENTAGES);

  const plannedValues = buildPlannedMatrix({
    referenceValues,
    planPercentages,
    metrics,
    jDays,
  });

  const actualValues = overlayPeakSpeedActuals(
    buildActualMatrix({
      referenceValues,
      planPercentages,
      metrics,
      jDays,
      seed,
      dayFactors,
    }),
    referenceValues,
    jDays,
    seed + matchIndex * 17,
  );

  const externalMetrics = filterWeeklyRecapLoadMetrics(
    WEEKLY_RECAP_REFERENCE_METRICS.filter((m) =>
      metrics.some((entry) => entry.key === m.key),
    ),
  );
  const internalMetrics = WEEKLY_RECAP_INTERNAL_METRICS.filter((m) =>
    metrics.some((entry) => entry.key === m.key),
  );

  const similarityByMetric = computeSimilarityByMetric({
    plannedMatrix: plannedValues,
    actualMatrix: actualValues,
    metrics: [...externalMetrics, ...internalMetrics],
    jDays: jDays.filter((day) => day !== "MD"),
  });

  const externalWeeklySimilarity = computeAverageSimilarity(
    externalMetrics.map((metric) => similarityByMetric[metric.key]),
  );
  const internalWeeklySimilarity = computeAverageSimilarity(
    internalMetrics.map((metric) => similarityByMetric[metric.key]),
  );
  const weeklySimilarity = externalWeeklySimilarity ?? internalWeeklySimilarity;

  const selectedMatch = buildAvailableTargetMatches(matchDates, resolvedTargetDate || matchDates[0])
    .find((item) => item.date === targetIso);

  const similarityHistory = buildSimilarityHistory({
    matchDates,
    selectedTargetIso: targetIso,
    seed,
    weeklySimilarity: weeklySimilarity ?? 78,
  });

  const vmaMaxByDay = actualValues?.vmax || {};

  return {
    playerId: player?.id,
    fetchedAt: new Date().toISOString(),
    source: "maquette_mock",
    sourceDetails: "Demo data for maquette club",
    endpoint: "mock://weekly-recap/player",
    jDays,
    referenceValues,
    planPercentages,
    plannedValues,
    actualValues,
    displayConfig: null,
    microcycle: normalizeMicrocycleConfig(null),
    calculateSimilarityFromDay: "MD+1",
    similarityByMetric,
    weeklySimilarity: weeklySimilarity !== null ? round(weeklySimilarity, 1) : null,
    externalWeeklySimilarity: externalWeeklySimilarity !== null ? round(externalWeeklySimilarity, 1) : null,
    internalWeeklySimilarity: internalWeeklySimilarity !== null ? round(internalWeeklySimilarity, 1) : null,
    targetMatchDate: targetIso,
    targetMatchOpponent: selectedMatch?.opponent || DEMO_OPPONENTS[0],
    availableTargetMatches: buildAvailableTargetMatches(matchDates, resolvedTargetDate || matchDates[0]),
    referenceMetricSources: Object.fromEntries(
      metrics.map((metric) => [
        metric.key,
        {
          match_date: targetIso,
          simulation_id: `mock-${seed}-${metric.key}`,
        },
      ]),
    ),
    referenceScope: "target_match",
    referenceMatchDate: targetIso,
    similarityHistory,
    dataCollection: {
      status: "ok",
      dated_simulations_count: jDays.filter((day) => day !== "MD").length,
      microcycle_coverage_pct: 100,
      missing_days: [],
      issues: [],
    },
    status: "ok",
    loadError: null,
    vmaMaxByDay,
    computedReferenceValues: referenceValues,
    referenceOverrides: {},
  };
}
